/**
 * MovementPlan — the precomputed, deterministic motion script for one placement.
 *
 * A plan is a pure timeline: given `currentTime` you can compute every actor's
 * exact pose with no history/state. That is what makes scrubbing work — drag
 * the clock anywhere and `computeActorPose` returns the right transform.
 *
 * Inbound legs are scheduled BACKWARD from `arrival` and outbound legs FORWARD
 * from `departure`, so the aircraft is provably parked across [arrival, departure]
 * exactly as the legacy snap behavior — motion only fills the lead-in / tail-out.
 */
import { EASES, lerpAngle, samplePolyline, type EaseFn, type Pt } from './easing'
import { compassToSceneRotation } from '../engine/heading'

/** Which physical thing a leg/dwell moves. */
export type ActorKind = 'aircraft' | 'tug' | 'fuel_truck'

export interface ActorRef {
  kind: ActorKind
  /** Scene object id: aircraft `evt:<id>`, tug `tug:<id>`, fuel truck `fuel:<id>`. */
  id: string
}

export type LegKind = 'final' | 'taxi' | 'tow' | 'tug_empty' | 'climb' | 'takeoff_roll' | 'landing_roll'

export interface MotionLeg {
  actorId: string
  kind: LegKind
  startT: number
  endT: number
  /** Waypoints in scene feet [x, z]; 2 points = straight segment. */
  path: Pt[]
  /** Easing key into EASES. */
  ease: keyof typeof EASES | string
  /** 'tangent' follows the path direction; 'fixed' holds `fixedHeadingDeg`. */
  headingMode: 'tangent' | 'fixed'
  fixedHeadingDeg?: number
  /** Blend heading FROM this at the leg start (e.g. ease out of the parked heading). */
  launchHeadingDeg?: number
  /** Blend heading toward this at the leg end (e.g. settle to parked heading). */
  settleHeadingDeg?: number
  /** Altitude feet at u=0 / u=1 (airborne legs). Defaults 0. */
  altStartFt?: number
  altEndFt?: number
}

export interface MotionDwell {
  actorId: string
  startT: number
  endT: number
  position: Pt
  headingDeg: number
  altFt?: number
}

export interface MovementPlan {
  eventAllocationId: string
  startT: number
  endT: number
  actors: ActorRef[]
  legs: MotionLeg[]
  dwells: MotionDwell[]
}

export interface ActorPose {
  /** scene feet [x, y, z] — y carries altitude */
  position: [number, number, number]
  rotationDeg: number
}

/** Full visible span of a plan = [startT, endT]. */
export function planSpan(plan: MovementPlan): [number, number] {
  return [plan.startT, plan.endT]
}

function legPose(leg: MotionLeg, t: number): ActorPose {
  const span = leg.endT - leg.startT
  const u = span <= 0 ? 1 : Math.max(0, Math.min(1, (t - leg.startT) / span))
  const ease: EaseFn = EASES[leg.ease] ?? EASES.inOut
  const e = ease(u)
  const { position, tangentDeg } = samplePolyline(leg.path, e)

  let heading = leg.headingMode === 'fixed' ? (leg.fixedHeadingDeg ?? tangentDeg) : tangentDeg
  if (leg.launchHeadingDeg != null) {
    // Blend FROM the launch heading over the first 25% of the leg.
    const k = Math.min(1, e / 0.25)
    heading = lerpAngle(leg.launchHeadingDeg, heading, k)
  }
  if (leg.settleHeadingDeg != null) {
    // Blend to the settle heading over the last 25% of the leg.
    const k = Math.max(0, (e - 0.75) / 0.25)
    heading = lerpAngle(heading, leg.settleHeadingDeg, k)
  }

  // Flare: on the descending final, ease the sink rate near touchdown so the
  // aircraft settles onto the runway instead of diving at a constant angle.
  const altE = leg.kind === 'final' ? 1 - Math.pow(1 - e, 1.6) : e
  const alt = (leg.altStartFt ?? 0) + ((leg.altEndFt ?? 0) - (leg.altStartFt ?? 0)) * altE
  return { position: [position[0], alt, position[1]], rotationDeg: heading }
}

function dwellPose(d: MotionDwell): ActorPose {
  return { position: [d.position[0], d.altFt ?? 0, d.position[1]], rotationDeg: d.headingDeg }
}

/**
 * Output boundary: the motion module computes every heading in the compass
 * convention (0 = north, see easing.ts), but the placement source (`p.rotation`)
 * and the renderer (`updateModelTransform`) speak the scene Y-rotation convention
 * (0 = +Z/south). Convert here, once, so all callers receive a scene rotation.
 * (`p.rotation` is pre-converted to compass at the builder input, so a parked
 * dwell round-trips back to its exact placement heading.)
 */
function toSceneRotation(pose: ActorPose): ActorPose {
  return { position: pose.position, rotationDeg: compassToSceneRotation(pose.rotationDeg) }
}

/**
 * Compute an actor's pose at sim time `t`. Returns null if this actor is not
 * present at `t` (so the caller can hide/skip it — e.g. a tug between tows).
 */
export function computeActorPose(plan: MovementPlan, actorId: string, t: number): ActorPose | null {
  // Active leg?
  let chosen: MotionLeg | null = null
  for (const leg of plan.legs) {
    if (leg.actorId !== actorId) continue
    if (t >= leg.startT && t < leg.endT) {
      chosen = leg
      break
    }
  }
  if (chosen) return toSceneRotation(legPose(chosen, t))

  // Active dwell?
  for (const d of plan.dwells) {
    if (d.actorId !== actorId) continue
    if (t >= d.startT && t < d.endT) return toSceneRotation(dwellPose(d))
  }

  // Not present at t.
  return null
}

/** Is the actor moving (on a leg) at t? Used to skip per-frame updates of static actors. */
export function isActorMoving(plan: MovementPlan, actorId: string, t: number): boolean {
  for (const leg of plan.legs) {
    if (leg.actorId === actorId && t >= leg.startT && t < leg.endT) return true
  }
  return false
}

/** Live counts at time t: aircraft taxiing/flying, tugs present, fuel trucks present. */
export function countActiveActors(
  plans: Record<string, MovementPlan>,
  t: number
): { movingAircraft: number; activeTugs: number; activeFuelTrucks: number } {
  let movingAircraft = 0
  let activeTugs = 0
  let activeFuelTrucks = 0
  for (const id in plans) {
    const plan = plans[id]
    for (const actor of plan.actors) {
      if (actor.kind === 'tug') {
        if (computeActorPose(plan, actor.id, t)) activeTugs++
      } else if (actor.kind === 'fuel_truck') {
        if (computeActorPose(plan, actor.id, t)) activeFuelTrucks++
      } else if (isActorMoving(plan, actor.id, t)) {
        movingAircraft++
      }
    }
  }
  return { movingAircraft, activeTugs, activeFuelTrucks }
}
